import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../services/api.service';

@Component({
  standalone: true,
  selector: 'app-admin',
  imports: [CommonModule, FormsModule, RouterModule],
  template: `
<h4>Admin</h4>

<div class="row g-3">
  <div class="col-12 col-lg-6">
    <h5>Festivals</h5>
    <div class="d-flex gap-2 mb-2">
      <input class="form-control" placeholder="ID" [(ngModel)]="newFest.id">
      <input class="form-control" placeholder="Name" [(ngModel)]="newFest.name">
      <input class="form-control" placeholder="Bild-URL" [(ngModel)]="newFest.imageUrl">
      <button class="btn btn-primary" (click)="createFestival()">Anlegen</button>
    </div>
    <ul class="list-group mb-3">
      <li class="list-group-item d-flex align-items-center gap-2" *ngFor="let f of festivals">
        <img *ngIf="f.imageUrl" [src]="f.imageUrl" style="height:28px;width:28px;object-fit:cover;border-radius:4px;">
        <input class="form-control form-control-sm" [(ngModel)]="f.name" style="max-width:200px">
        <input class="form-control form-control-sm" [(ngModel)]="f.imageUrl" placeholder="Bild-URL">
        <input type="file" class="form-control form-control-sm" accept="image/*" (change)="onSelectImage($event, f)">
        <button class="btn btn-sm btn-success" (click)="updateFestival(f)">Speichern</button>
        <button class="btn btn-sm btn-danger" (click)="deleteFestival(f)">Löschen</button>
      </li>
    </ul>
  </div>

  <div class="col-12 col-lg-6">
    <h5>Bands Import (Wacken JSON)</h5>
    <div class="mb-2">
      <label class="form-label">Festival wählen</label>
      <select class="form-select" [(ngModel)]="importFestivalId">
        <option [ngValue]="''" disabled>Bitte Festival wählen</option>
        <option *ngFor="let f of festivals" [ngValue]="f.id">{{ f.name }} ({{ f.id }})</option>
      </select>
    </div>
    <div class="mb-2">
      <textarea class="form-control" [(ngModel)]="importJson" placeholder='JSON Array...' rows="8"></textarea>
    </div>
    <button class="btn btn-outline-primary" (click)="doImport()" [disabled]="!importFestivalId">Importieren</button>
    <div class="text-success mt-2" *ngIf="okImport">Import erfolgreich</div>
    <div class="text-danger mt-2" *ngIf="errImport">{{ errImport }}</div>
  </div>

  <div class="col-12 col-lg-6">
    <h5>Bands bearbeiten</h5>
    <div class="mb-2">
      <label class="form-label">Festival wählen</label>
      <div class="d-flex gap-2">
        <select class="form-select" [(ngModel)]="bandsFestivalId">
          <option [ngValue]="''" disabled>Bitte Festival wählen</option>
          <option *ngFor="let f of festivals" [ngValue]="f.id">{{ f.name }} ({{ f.id }})</option>
        </select>
        <a class="btn btn-outline-primary" [routerLink]="['/admin/festivals', bandsFestivalId, 'bands']" [class.disabled]="!bandsFestivalId" [attr.aria-disabled]="!bandsFestivalId">Öffnen</a>
      </div>
      <div class="form-text">Öffnet eine Seite mit Tabelle zur Inline-Bearbeitung.</div>
    </div>
  </div>

  <div class="col-12 col-lg-6">
    <h5>Standard-Bezeichner (1-5)</h5>
    <div class="d-flex flex-wrap align-items-end gap-2">
      <div *ngFor="let i of [0,1,2,3,4]">
        <label class="form-label small">{{ i+1 }}</label>
        <input class="form-control form-control-sm" [(ngModel)]="defaultLabels[i]" style="min-width:160px;">
      </div>
      <button class="btn btn-sm btn-success" (click)="saveDefaultLabels()">Speichern</button>
    </div>
    <div class="text-success mt-2" *ngIf="okDefaultLabels">Gespeichert</div>
    <div class="text-danger mt-2" *ngIf="errDefaultLabels">{{ errDefaultLabels }}</div>
  </div>
</div>
  `
})
export class AdminComponent {
  festivals: any[] = [];
  newFest: any = { id: '', name: '', imageUrl: '' };
  importFestivalId = '';
  importJson = '';
  okImport = false;
  errImport = '';

  // Bands Editor Navigation
  bandsFestivalId = '';

  // Default labels
  defaultLabels: string[] = ['geht gar nicht', 'eher nicht', 'weiß ich noch nicht', 'kann man mal hören', 'unbedingt anhören'];
  okDefaultLabels = false;
  errDefaultLabels = '';

  constructor(private api: ApiService) {
    this.reloadFestivals();
  }

  async reloadFestivals() {
    try {
      this.festivals = await this.api.adminFestivals();
      if (this.festivals.length && !this.importFestivalId) {
        this.importFestivalId = this.festivals[0].id;
      }
      if (this.festivals.length && !this.bandsFestivalId) this.bandsFestivalId = this.festivals[0].id;
    } catch {
      this.festivals = [];
    }
  }

  async createFestival() {
    await this.api.adminFestivalCreate(this.newFest.id, this.newFest.name, this.newFest.imageUrl || undefined);
    this.newFest = { id: '', name: '', imageUrl: '' };
    await this.reloadFestivals();
  }

  async updateFestival(f: any) {
    await this.api.adminFestivalUpdate(f.id, { name: f.name, imageUrl: f.imageUrl });
    await this.reloadFestivals();
  }

  async deleteFestival(f: any) {
    const label = f.name ? `${f.name} (${f.id})` : f.id;
    const ok = window.confirm(`Festival wirklich löschen?\n${label}`);
    if (!ok) return;
    await this.api.adminFestivalDelete(f.id);
    await this.reloadFestivals();
  }

  async doImport() {
    this.okImport = false;
    this.errImport = '';
    try {
      const data = JSON.parse(this.importJson);
      if (!Array.isArray(data)) throw new Error('JSON ist kein Array');
      await this.api.adminBandsImport(this.importFestivalId, data);
      this.okImport = true;
    } catch (e: any) {
      this.errImport = e?.message || 'Fehler beim Import';
    }
  }

  async onSelectImage(ev: Event, f: any) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      // 1) Upload-URL anfordern
      const { uploadUrl, objectUrl } = await this.api.adminFestivalImageUploadUrl(f.id, file.type);
      // 2) Upload durchführen
      await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      // 3) Festival speichern mit neuer Bild-URL
      f.imageUrl = objectUrl;
      await this.updateFestival(f);
    } catch (e: any) {
      alert('Bild-Upload fehlgeschlagen: ' + (e?.message || e));
    } finally {
      // Reset des File-Inputs
      input.value = '';
    }
  }

  // Bands-Verwaltung erfolgt auf separater Seite

  async saveDefaultLabels() {
    try {
      this.okDefaultLabels = false;
      this.errDefaultLabels = '';
      if (this.defaultLabels.length !== 5) throw new Error('Es müssen 5 Bezeichner gesetzt sein.');
      await this.api.adminDefaultLabelsPut(this.defaultLabels);
      this.okDefaultLabels = true;
    } catch (e: any) {
      this.errDefaultLabels = e?.message || 'Speichern fehlgeschlagen';
    }
  }
}
